import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const WaitlistForm = () => {
    const [email, setEmail] = useState("");
    const [state, setState] = useState("idle");
    const [error, setError] = useState("");
    const [count, setCount] = useState(0);

    useEffect(() => {
        axios
            .get(`${API}/waitlist/count`)
            .then((r) => setCount(r.data.count))
            .catch(() => {});
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        if (!EMAIL_RE.test(email)) {
            setError("Enter a valid email address.");
            return;
        }
        setError("");
        setState("loading");
        try {
            const r = await axios.post(`${API}/waitlist`, { email });
            setCount(r.data.count);
            setState("success");
            toast.success("You're on the list.");
        } catch (err) {
            setState("idle");
            if (err?.response?.status === 409) {
                setError("You're already on the list — no need to join twice.");
            } else {
                setError("Something went wrong. Please try again.");
            }
        }
    };

    return (
        <div className="mx-auto w-full max-w-md">
            <AnimatePresence mode="wait" initial={false}>
                {state === "success" ? (
                    <motion.div
                        key="success"
                        data-testid="waitlist-success"
                        initial={{ opacity: 0, y: 16, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                        className="flex items-center gap-4 rounded-2xl border border-acid/40 bg-acid/10 p-5 text-left"
                    >
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-acid">
                            <Check size={20} strokeWidth={3} className="text-ink" />
                        </span>
                        <span>
                            <span className="block font-bold text-white">
                                You're in.
                            </span>
                            <span className="block text-sm text-[#9c9c96]">
                                We'll email {email} when doors open.
                            </span>
                        </span>
                    </motion.div>
                ) : (
                    <motion.form
                        key="form"
                        onSubmit={submit}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0, y: -12 }}
                        className="flex flex-col gap-3"
                        noValidate
                    >
                        <input
                            type="email"
                            inputMode="email"
                            autoComplete="email"
                            data-testid="waitlist-email-input"
                            value={email}
                            onChange={(e) => {
                                setEmail(e.target.value);
                                if (error) setError("");
                            }}
                            placeholder="your@email.com"
                            className="h-14 w-full rounded-2xl border border-white/[0.09] bg-[#141416] px-5 text-center text-base text-white placeholder:text-white/30 transition-all duration-300 focus:border-acid/60 focus:outline-none focus:ring-2 focus:ring-acid/20"
                        />
                        <button
                            type="submit"
                            data-testid="waitlist-submit-button"
                            disabled={state === "loading"}
                            className="flex h-14 w-full touch-manipulation items-center justify-center gap-2 rounded-2xl bg-acid font-extrabold text-ink shadow-glow-sm transition-all duration-300 hover:brightness-110 hover:shadow-glow active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-70"
                        >
                            {state === "loading" ? (
                                <>
                                    <Loader2 size={18} className="animate-spin" />
                                    Joining…
                                </>
                            ) : (
                                "Join the waitlist"
                            )}
                        </button>
                    </motion.form>
                )}
            </AnimatePresence>

            {state !== "success" && error && (
                <motion.p
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    data-testid="waitlist-error"
                    className="mt-3 text-sm text-red-400"
                >
                    {error}
                </motion.p>
            )}

            {count > 0 && (
                <p
                    data-testid="waitlist-count"
                    className="mt-5 text-xs tracking-wide text-white/35"
                >
                    {count} {count === 1 ? "person" : "people"} already on the
                    list
                </p>
            )}
        </div>
    );
};
